/**
 * The reporting vocabulary this app renders with, and the arithmetic that turns
 * what somebody picked into the window that gets stored on the run.
 *
 * **Nothing here decides what a run means.** The four status labels, the failure
 * sentences, the caption a sheet carries and the rule for whether a run has a
 * file all live in `@wend/shared`'s `report.ts` and are imported, never
 * restated: `REPORT_RUN_STATUS_LABELS`, `reportRunStatusDetail`,
 * `reportRunArtifact`, `describeReportWindow`. What this file adds is the two
 * things a contract cannot hold — which colour a status wears, and how a date
 * picker becomes a `ReportWindow` — plus the one thing it must not get wrong.
 *
 * **The one thing it must not get wrong: the boundaries.** A window is half-open
 * `[from, to)`, and a person picking dates does not think in half-open
 * intervals — they think "the 1st to the 30th, including the 30th". So
 * {@link customReportWindow} takes the *inclusive* last day and converts it,
 * and the screen prints `describeReportWindow` underneath so the reader sees the
 * exact boundaries that will be stored rather than trusting this conversion.
 * The same mismatch is deliberate in `reportFileName` for the same reason, and
 * it is documented there: `…-to-2026-10-01.xlsx` on a file with nothing from
 * October is the small lie that gets a correct report deleted as wrong.
 *
 * **The two recurring rules are resolved with the scheduler's own function.**
 * `resolveReportWindow` is what the nightly schedule calls at tick time, and it
 * is what this app calls when somebody picks "the month that just ended" — so a
 * run started from this screen and a run started by a schedule cover *the same
 * instants*, not two windows that happen to agree today. Re-deriving "last
 * month" here with `setMonth(-1)` would be a second opinion about where a month
 * starts, and it is wrong twice a year in a way nobody notices until two reports
 * are added together.
 *
 * **A zone that could not be named is not silently UTC.** `browserTimeZone()`
 * from `campaign-display.ts` is reused rather than re-implemented, and its
 * `resolved: false` is surfaced by the screen as a sentence. The instants are
 * still the ones the platform computed from the local wall clock either way; it
 * is the *name* stored beside them that becomes a guess, and a window whose
 * printed boundaries are not midnight is how a reader sees that for themselves.
 */
import {
  reportWindowDurationDays,
  resolveReportWindow,
  type ReportRun,
  type ReportWindow,
  type ReportWindowRule,
} from '@wend/shared';
import type { BadgeTone } from '@wend/ui';

import { pluralise } from './format';

/* -------------------------------------------------------------------------- */
/* Status colour                                                               */
/* -------------------------------------------------------------------------- */

/**
 * One tone per run status, total over the union — a fifth status would fail this
 * table at compile time rather than rendering as an uncoloured chip.
 *
 * `failed` is `bad` and `queued` is `neutral`, which is the distinction the
 * whole slice turns on: a run that has not started yet is *not* a problem, and a
 * run that failed is not an absence. Colour is never the only carrier — the
 * badge prints `REPORT_RUN_STATUS_LABELS[status]` inside it, so "Failed" is
 * legible with no colour at all.
 */
export const REPORT_RUN_TONE = {
  queued: 'neutral',
  running: 'info',
  succeeded: 'good',
  failed: 'bad',
} as const satisfies Readonly<Record<ReportRun['status'], BadgeTone>>;

/* -------------------------------------------------------------------------- */
/* Picking a period                                                            */
/* -------------------------------------------------------------------------- */

/**
 * What the period control offers: the two recurring rules a schedule can hold,
 * and an explicit pair of dates.
 *
 * The two rules come first, and they are the same members as
 * `REPORT_WINDOW_RULE_VALUES` rather than a parallel list, so a third rule added
 * to the contract cannot be silently missing from this screen. `custom` is not
 * a rule and never becomes one: a schedule holds a rule, and "1 September to 30
 * September" is not something that can recur.
 */
export const REPORT_PERIOD_CHOICE_VALUES = ['previous_day', 'previous_month', 'custom'] as const;
export type ReportPeriodChoice = (typeof REPORT_PERIOD_CHOICE_VALUES)[number];

/** True for the two choices that are also schedule rules. Narrows for the caller. */
export function isReportWindowRule(choice: ReportPeriodChoice): choice is ReportWindowRule {
  return choice !== 'custom';
}

/** `2026-09-05` — what an `<input type="date">` reads and writes, in local time. */
export function toDateInputValue(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The `YYYY-MM-DD` a date input holds, as `[year, month, day]`, or `null`. */
function readDateInputValue(value: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // `new Date(2026, 1, 31)` is 3 March, not an error, so the round trip below is
  // what refuses a day that does not exist rather than quietly moving it.
  const candidate = new Date(year, month - 1, day);
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }
  return [year, month, day];
}

/**
 * The window for two dates a person picked, where `lastDay` is the day they mean
 * to be **included**.
 *
 * `to` is midnight at the start of the day *after* `lastDay`, because the window
 * is half-open — that is the only convention under which two consecutive reports
 * tile with no gap and no overlap, and a movement at exactly midnight lands in
 * precisely one of them. Both instants are built by the platform from the local
 * wall clock, which is the same thing `datetime-local` does on the campaign
 * screens and the only reading under which the zone named beside them is honest.
 *
 * Returns `null` when either date is unreadable — a caller shows the field
 * error instead. It does **not** check that `from` precedes `to` or that the
 * window is short enough: `reportWindowSchema` owns both rules, and a second
 * copy of them here would be a second opinion about what this app may ask for.
 */
export function customReportWindow(
  firstDay: string,
  lastDay: string,
  timezone: string,
): ReportWindow | null {
  const first = readDateInputValue(firstDay);
  const last = readDateInputValue(lastDay);
  if (first === null || last === null) return null;

  const from = new Date(first[0], first[1] - 1, first[2]);
  // Day + 1: JavaScript normalises the overflow, so 31 December becomes 1
  // January of the next year without this file knowing how long a month is.
  const to = new Date(last[0], last[1] - 1, last[2] + 1);

  return { from: from.toISOString(), to: to.toISOString(), timezone };
}

/**
 * The window a choice names, or `null` when it cannot be established.
 *
 * A `null` from `resolveReportWindow` means the platform would not say what
 * offset the zone was at, and the contract's rule for that is to refuse rather
 * than to fall back to UTC — a report bucketed against the wrong clock is
 * confidently wrong, and it outlives the screen it came from. The screen turns
 * this `null` into a sentence and disables the button; it never sends a window
 * it could not compute.
 */
export function reportWindowForChoice(
  choice: ReportPeriodChoice,
  now: Date,
  timezone: string,
  custom: { firstDay: string; lastDay: string },
): ReportWindow | null {
  if (isReportWindowRule(choice)) return resolveReportWindow(choice, now, timezone);
  return customReportWindow(custom.firstDay, custom.lastDay, timezone);
}

/* -------------------------------------------------------------------------- */
/* Saying how long something is, and how long something took                   */
/* -------------------------------------------------------------------------- */

/**
 * Roughly how long a window is, for a person sanity-checking the dates they
 * typed. Never used for anything the report is computed from.
 *
 * It says "about" whenever the span is not a whole number of days, which is not
 * pedantry: a window across a daylight-saving boundary really is 23 or 25 hours
 * long, and a screen that rounds that away is a screen that will be quoted back
 * when two reports do not add up.
 */
export function reportWindowLengthLabel(window: ReportWindow): string {
  const days = reportWindowDurationDays(window);
  const whole = Math.round(days);
  const exact = Math.abs(days - whole) < 1e-9;
  if (whole >= 1) return exact ? pluralise(whole, 'day') : `about ${pluralise(whole, 'day')}`;
  return 'less than a day';
}

/**
 * How long a run took, from its own two timestamps.
 *
 * Milliseconds below a second, because a report that finished in 40ms and one
 * that finished in 900ms both reading "0s" hides the only signal anybody has
 * that a run did no work.
 *
 * A run that started and finished inside the same millisecond — a
 * `window_unresolvable` failure reads nothing at all — says so in words rather
 * than as `0 ms`, which reads as a measurement that failed rather than as a run
 * that was over before the clock could tell.
 */
export function formatDurationMs(ms: number): string {
  if (ms < 1) return 'under a millisecond';
  if (ms < 1_000) return `${String(Math.round(ms))} ms`;

  const seconds = ms / 1_000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;

  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds - minutes * 60);
  // 59.6s rounds to 60 and "3m 60s" is not a time anybody writes.
  return rest === 60 ? `${String(minutes + 1)}m 0s` : `${String(minutes)}m ${String(rest)}s`;
}
